// src/pages/ForgotPassword.tsx
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import logoImg from '../assets/images/Logo-Font-Color-NB.png';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const { resetPassword, error, setError } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      setError('Please enter your email address');
      return;
    }
    
    try {
      setMessage('');
      setError(null);
      setIsSubmitting(true);
      await resetPassword(email);
      setMessage('Password reset link has been sent to your email. Please check your inbox and follow the instructions.');
    } catch (error) {
      console.error('Password reset error:', error);
      // Error handling is already done in AuthContext
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <img
            src={logoImg}
            alt="Orlo Logo"
            className="mx-auto h-16 w-auto"
          />
          <h2 className="mt-4 text-2xl font-semibold text-neutral-800">
            Reset your password
          </h2>
          <p className="mt-2 text-base text-neutral-600">
            Enter your email and we'll send you a password reset link
          </p>
        </div>
        
        {error && (
          <div className="bg-error-100 border border-error-200 text-error-800 px-4 py-3 rounded-md">
            {error}
          </div>
        )}
        
        {message && (
          <div className="bg-success-100 border border-success-200 text-success-800 px-4 py-3 rounded-md">
            {message}
          </div>
        )}
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div>
            <label htmlFor="email-address" className="block text-sm font-medium text-neutral-700">Email address</label>
            <input
              id="email-address"
              name="email"
              type="email"
              autoComplete="email"
              required
              className="mt-1 appearance-none rounded-md relative block w-full px-3 py-2 border border-neutral-300 placeholder-neutral-500 text-neutral-900 focus:outline-none focus:ring-brand-blue focus:border-brand-blue sm:text-sm"
              placeholder="Your email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-sky-500 hover:bg-sky-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 disabled:opacity-70"
            >
              {isSubmitting ? 'Processing...' : 'Send reset link'}
            </button>
          </div>
        </form>
        
        <div className="flex items-center justify-between mt-4">
          <div className="text-sm">
            <Link 
              to="/login" 
              className="font-medium text-brand-blue hover:text-brand-purple"
            >
              &larr; Back to sign in
            </Link>
          </div>
          
          <div className="text-sm">
            <Link 
              to="/register" 
              className="font-medium text-brand-blue hover:text-brand-purple"
            >
              Create a new account
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;